import { Link } from "react-router";
import { Adjuntos, Badge, Button, Card, Cronograma, DataList, Desplegable, GraficoCurva, MetricCard, Row, Stack } from "@/components";
import type { Adjunto, AvanceAdmin, HitoAvance, RubroAvance, TareaAvance } from "@/lib/api";
import { barrasTareas, diasDesdeEpoca, estadoVisible, formatFechaCorta, formatPct, formatPuntos, puntosGrafico, rubrosAgrupados, type PuntoGrafico } from "@/lib/avance";
import { formatFecha, hoyISO } from "@/lib/format";

export interface ControlInternoProps {
  admin: AvanceAdmin;
  hayTareas: boolean;
  onNuevaNovedad: () => void;
  onInicio: () => void;
  rutaDescargaGantt: (a: Adjunto) => string;
  onSubirGantt: (archivo: File) => Promise<unknown>;
  onBorrarGantt: (a: Adjunto) => Promise<unknown>;
  subiendoGantt: boolean;
  errorGantt: string | null;
}

const COLUMNAS_CURVA = [
  { clave: "fecha", titulo: "Fecha", render: (p: PuntoGrafico) => formatFecha(p.fecha) },
  { clave: "planificado", titulo: "Planificado", render: (p: PuntoGrafico) => formatPct(p.planificado) },
  { clave: "real", titulo: "Real", render: (p: PuntoGrafico) => (p.real === null ? "—" : formatPct(p.real)) },
];

function tituloRubro(r: RubroAvance): string {
  const rango = r.inicio_plan && r.fin_plan ? ` · ${formatFecha(r.inicio_plan)} al ${formatFecha(r.fin_plan)}` : "";
  return `${r.nombre} · ${formatPct(r.avance)}${rango}`;
}

function ListaTareasSimple({ etiqueta, tareas }: { etiqueta: string; tareas: TareaAvance[] }) {
  return (
    <ul aria-label={etiqueta} className="flex flex-col gap-2">
      {tareas.map((t) => {
        const visible = estadoVisible(t);
        return (
          <li key={t.id} className="flex items-center justify-between gap-3">
            <div>
              <span className="font-medium">{t.nombre}</span>
              <span className="block text-chico text-texto-suave">{`${t.duracion} días hábiles`}</span>
            </div>
            <Badge tono={visible.tono}>{visible.texto}</Badge>
          </li>
        );
      })}
    </ul>
  );
}

function FilaHito({ hito }: { hito: HitoAvance }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div>
        <span className="font-medium">{`🏁 ${hito.nombre}`}</span>
        {hito.fecha_plan && <span className="block text-chico text-texto-suave">{`Estimado: ${formatFecha(hito.fecha_plan)}`}</span>}
      </div>
      {hito.cumplido_el && <Badge tono="exito">{`Cumplido el ${formatFecha(hito.cumplido_el)}`}</Badge>}
    </li>
  );
}

/**
 * Control interno del admin: plan contra real, curva, el avance de cada rubro del cronograma con sus hitos y el Gantt
 * adjunto. Sólo lo ven los administradores (el backend ya no manda estos datos a los demás roles). Las tareas, su
 * duración y sus predecesoras se cargan en Obra → Cronograma.
 */
export function ControlInterno({ admin, hayTareas, onNuevaNovedad, onInicio, rutaDescargaGantt, onSubirGantt, onBorrarGantt, subiendoGantt, errorGantt }: ControlInternoProps) {
  const grupos = rubrosAgrupados(admin);
  const puntos = puntosGrafico(admin.curva);
  const hoy = diasDesdeEpoca(hoyISO());
  const resumen = admin.resumen;

  return (
    <section aria-labelledby="control-interno">
      <Stack espacio={4}>
        <Card>
          <Stack espacio={3}>
            <Stack espacio={1}>
              <h2 id="control-interno" className="text-titulo font-light tracking-tight">
                Control interno
              </h2>
              <p className="text-chico text-texto-suave">Sólo lo ven los administradores.</p>
            </Stack>
            <Row envolver espacio={2}>
              {hayTareas && <Button onClick={onNuevaNovedad}>Nueva novedad</Button>}
              <Button variante="secundario" onClick={onInicio}>
                {admin.inicio ? "Cambiar fecha de inicio" : "Fijar fecha de inicio"}
              </Button>
            </Row>
            <p>
              {admin.inicio
                ? `Inicio de obra: ${formatFecha(admin.inicio)}${admin.fin_estimado ? ` · Fin estimado: ${formatFecha(admin.fin_estimado)}` : ""}`
                : "Inicio de obra: a definir (se fija con la aprobación de planos)"}
            </p>
            <p className="text-chico text-texto-suave">
              El avance se mide sobre las tareas del{" "}
              <Link to="/cronograma" className="text-primario underline-offset-2 hover:underline">
                cronograma
              </Link>{" "}
              (los ítems del presupuesto con duración), pesadas por sus días hábiles.
            </p>
          </Stack>
        </Card>

        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard titulo="Avance real" valor={resumen ? formatPct(resumen.real) : "a definir"} />
          <MetricCard titulo="Planificado a hoy" valor={resumen?.planificado != null ? formatPct(resumen.planificado) : "a definir"} />
          <MetricCard
            titulo="Diferencia"
            valor={resumen?.diferencia != null ? formatPuntos(resumen.diferencia) : "a definir"}
            className={resumen?.diferencia != null && Number(resumen.diferencia) < 0 ? "text-peligro" : undefined}
          />
        </div>

        {puntos.length > 0 && (
          <>
            <Card>
              <Stack espacio={4}>
                <h3 className="text-subtitulo font-medium">Curva de avance</h3>
                <GraficoCurva
                  etiqueta="Curva de avance planificado y real"
                  puntos={puntos}
                  hoy={hoy}
                  formatValor={formatPct}
                  formatEje={(dias) => formatFechaCorta(new Date(dias * 86_400_000).toISOString())}
                />
              </Stack>
            </Card>
            <DataList etiqueta="Avance por fecha" columnas={COLUMNAS_CURVA} filas={puntos} claveFila={(p) => p.fecha} />
          </>
        )}

        {grupos.length > 0 && (
          <Card>
            <Stack espacio={4}>
              <h3 className="text-subtitulo font-medium">Avance por rubro</h3>
              <Stack espacio={3}>
                {grupos.map((g) => {
                  const etiquetaTareas = `Tareas de ${g.rubro.nombre}`;
                  const barras = admin.inicio ? barrasTareas(g.tareas, admin.inicio, admin.fin_estimado) : null;
                  return (
                    <Desplegable key={g.rubro.id} abiertoInicial={g.abierta} titulo={tituloRubro(g.rubro)}>
                      <Stack espacio={4}>
                        {barras ? (
                          <Cronograma etiqueta={etiquetaTareas} etapas={barras.etapas} hoy={barras.hoy} />
                        ) : (
                          <ListaTareasSimple etiqueta={etiquetaTareas} tareas={g.tareas} />
                        )}
                        {g.hitos.length > 0 && (
                          <ul className="flex flex-col gap-2">
                            {g.hitos.map((h) => (
                              <FilaHito key={h.id} hito={h} />
                            ))}
                          </ul>
                        )}
                      </Stack>
                    </Desplegable>
                  );
                })}
              </Stack>
            </Stack>
          </Card>
        )}

        <Card>
          <Adjuntos
            etiqueta="Gantt de la obra (PDF o imagen)"
            adjuntos={admin.cronograma_adjuntos}
            rutaDescarga={rutaDescargaGantt}
            puedeEditar
            onSubir={onSubirGantt}
            onBorrar={onBorrarGantt}
            subiendo={subiendoGantt}
            error={errorGantt}
          />
        </Card>
      </Stack>
    </section>
  );
}
